import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  CreditCard,
  Lock,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const planName = location.state?.planName || 'Basic';
  
  const [formData, setFormData] = useState({
    cardHolderName: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    // Format card number with spaces
    if (field === 'cardNumber') {
      formattedValue = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
      if (formattedValue.length > 19) formattedValue = formattedValue.slice(0, 19);
    }
    
    // Format expiry month (2 digits max)
    if (field === 'expiryMonth') {
      formattedValue = value.replace(/\D/g, '').slice(0, 2);
    }
    
    // Format expiry year (2 digits max)
    if (field === 'expiryYear') {
      formattedValue = value.replace(/\D/g, '').slice(0, 2);
    }
    
    // Format CVC (3-4 digits max)
    if (field === 'cvc') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cardHolderName.trim()) {
      newErrors.cardHolderName = 'Card holder name is required';
    }

    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 16) {
      newErrors.cardNumber = 'Valid card number is required';
    }

    if (!formData.expiryMonth || formData.expiryMonth.length !== 2) {
      newErrors.expiryMonth = 'Valid month is required';
    }

    if (!formData.expiryYear || formData.expiryYear.length !== 2) {
      newErrors.expiryYear = 'Valid year is required';
    }

    if (!formData.cvc || formData.cvc.length < 3) {
      newErrors.cvc = 'Valid CVC is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentSuccess(true);
      
      // Activate subscription and save plan details
      localStorage.setItem('subscriptionStatus', 'active');
      localStorage.setItem('userPlan', planName);
      localStorage.removeItem('subscriptionCanceledDate');
      
      // Set remaining analyses based on plan
      if (planName === 'Pro') {
        localStorage.setItem('remainingAnalyses', 'unlimited');
      } else if (planName === 'Plus') {
        localStorage.setItem('remainingAnalyses', '20');
        localStorage.setItem('todayAnalyses', '0');
      } else { // Basic
        localStorage.setItem('remainingAnalyses', '10');
        localStorage.setItem('todayAnalyses', '0');
      }
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('subscriptionUpdated', { 
        detail: { plan: planName, status: 'active' } 
      }));
      window.dispatchEvent(new CustomEvent('subscriptionChange', { 
        detail: { plan: planName, status: 'active' } 
      }));
    }, 2000);
  };

  const handleBack = () => {
    navigate('/billing');
  };

  const getCardType = (number) => {
    const num = number.replace(/\s/g, '');
    if (/^4/.test(num)) return 'VISA';
    if (/^5[1-5]/.test(num)) return 'MASTERCARD';
    if (/^3[47]/.test(num)) return 'AMEX';
    return null;
  };

  if (paymentSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 48,
                    color: 'white',
                  }}
                >
                  ✓
                </Typography>
              </Box>
              
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#2c3e50',
                }}
              >
                Payment Successful!
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(44, 62, 80, 0.7)',
                  mb: 4,
                }}
              >
                Your {planName} plan subscription has been activated successfully.
              </Typography>
              
              <Button
                variant="contained"
                onClick={() => {
                  // Reload to update subscription status
                  window.location.href = '/';
                }}
                sx={{
                  bgcolor: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                }}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        py: 8,
      }}
    >
      <Container maxWidth="sm">
        <IconButton
          onClick={handleBack}
          sx={{
            mb: 3,
            color: '#1976d2',
            '&:hover': {
              bgcolor: 'rgba(25, 118, 210, 0.1)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            bgcolor: 'white',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: '#2c3e50',
                }}
              >
                Complete Your Payment
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(44, 62, 80, 0.7)',
                }}
              >
                Subscribe to {planName} Plan
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {/* Card Holder Name */}
              <TextField
                label="Card Holder Name"
                fullWidth
                value={formData.cardHolderName}
                onChange={(e) => handleInputChange('cardHolderName', e.target.value)}
                error={!!errors.cardHolderName}
                helperText={errors.cardHolderName}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />

              {/* Card Number */}
              <Box>
                <TextField
                  label="Card Number"
                  fullWidth
                  value={formData.cardNumber}
                  onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                  error={!!errors.cardNumber}
                  helperText={errors.cardNumber}
                  placeholder="4110 - 0222 - 2389 - 2138"
                  InputProps={{
                    endAdornment: formData.cardNumber ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mr: 1,
                        }}
                      >
                        {getCardType(formData.cardNumber) && (
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: '#1976d2',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {getCardType(formData.cardNumber)}
                          </Box>
                        )}
                        <CreditCard sx={{ color: 'rgba(44, 62, 80, 0.5)' }} />
                      </Box>
                    ) : (
                      <CreditCard sx={{ color: 'rgba(44, 62, 80, 0.5)', mr: 1 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              </Box>

              {/* Expiry and CVC */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Expiry (MM/YY)"
                    fullWidth
                    value={formData.expiryMonth && formData.expiryYear 
                      ? `${formData.expiryMonth} / ${formData.expiryYear}`
                      : formData.expiryMonth || formData.expiryYear
                      ? `${formData.expiryMonth || ''} / ${formData.expiryYear || ''}`
                      : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s|\//g, '');
                      if (value.length <= 2) {
                        handleInputChange('expiryMonth', value);
                      } else {
                        handleInputChange('expiryMonth', value.slice(0, 2));
                        handleInputChange('expiryYear', value.slice(2, 4));
                      }
                    }}
                    placeholder="01 / YY"
                    error={!!errors.expiryMonth || !!errors.expiryYear}
                    helperText={errors.expiryMonth || errors.expiryYear}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="CVC"
                    fullWidth
                    type="password"
                    value={formData.cvc}
                    onChange={(e) => handleInputChange('cvc', e.target.value)}
                    error={!!errors.cvc}
                    helperText={errors.cvc}
                    placeholder="•••"
                    InputProps={{
                      endAdornment: (
                        <Box
                          sx={{
                            position: 'relative',
                            mr: 1,
                          }}
                        >
                          <CreditCard
                            sx={{
                              color: 'rgba(44, 62, 80, 0.5)',
                              fontSize: 20,
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 2,
                              right: 2,
                              width: 12,
                              height: 8,
                              border: '1px solid #1976d2',
                              borderRadius: 0.5,
                            }}
                          />
                        </Box>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isProcessing}
                sx={{
                  bgcolor: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  mt: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(25, 118, 210, 0.5)',
                  },
                }}
              >
                {isProcessing ? 'Processing...' : 'Submit'}
              </Button>

              {/* Security Message */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                <Lock sx={{ fontSize: 16, color: 'rgba(44, 62, 80, 0.5)' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(44, 62, 80, 0.5)',
                    fontSize: '0.75rem',
                  }}
                >
                  Payment securely processed
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Payment;
